import { Injectable, signal, NgZone, inject } from '@angular/core';
import { LoginData } from '../login-page/login-data';
import { environment } from '../../environments/environment';

// Message type constants - exported for use in sender applications
export const MESSAGE_TYPE = {
  ACCOUNT_DATA: 'ACCOUNT_DATA',
  READY_FOR_DATA: 'READY_FOR_DATA',
} as const;

export interface AccountDataMessage {
  type: typeof MESSAGE_TYPE.ACCOUNT_DATA;
  payload: LoginData;
}

@Injectable({
  providedIn: 'root',
})
export class TransferredAccountService {
  private readonly ngZone = inject(NgZone);
  private readonly accountDataSignal = signal<LoginData | null>(null);
  private messageListener?: (event: MessageEvent) => void;

  // Read-only access to the signal
  readonly accountData = this.accountDataSignal.asReadonly();

  /**
   * Start listening for account data from the parent application
   */
  startListening(): void {
    if (this.messageListener) {
      return; // Already listening
    }

    this.messageListener = (event: MessageEvent) => {
      const expectedOrigin = new URL(environment.accountInfoSenderUrl).origin;

      if (event.origin !== expectedOrigin) {
        return;
      }

      // Check if this is account data
      if (
        event.data &&
        typeof event.data === 'object' &&
        event.data.type === MESSAGE_TYPE.ACCOUNT_DATA
      ) {
        this.handleReceivedData(event.data);
      }
    };

    window.addEventListener('message', this.messageListener);
    TransferredAccountService.notifyReady();
  }

  /**
   * Stop listening for account data
   */
  stopListening(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = undefined;
    }
  }

  /**
   * Send a message to the parent window that we're ready to receive data
   */
  private static notifyReady(): void {
    if (window.opener || window.parent !== window) {
      const targetWindow = window.opener || window.parent;
      const expectedOrigin = new URL(environment.accountInfoSenderUrl).origin;

      targetWindow.postMessage(
        { type: MESSAGE_TYPE.READY_FOR_DATA, source: 'HNS_APP' },
        expectedOrigin,
      );
    }
  }

  /**
   * Handle received account data from the parent application
   */
  private handleReceivedData(data: AccountDataMessage): void {
    if (TransferredAccountService.isAccountData(data)) {
      this.ngZone.run(() => {
        const loginData = { ...data.payload };
        this.accountDataSignal.set(loginData);
      });
    }
  }

  /**
   * Check if the received data looks like account data
   */
  private static isAccountData(data: AccountDataMessage): data is AccountDataMessage {
    return (
      typeof data === 'object' &&
      data !== null &&
      ('firstName' in data.payload ||
        'lastName' in data.payload ||
        'dateOfBirth' in data.payload ||
        'rid' in data.payload ||
        'csrId' in data.payload)
    );
  }
}
